import { call, debounce, put, select, take, takeEvery } from 'redux-saga/effects';

import { getUserId } from '@elab/core/utils/user/sagaHelpers';
import { actions as orderActions } from '@elab/core/store/order';
import { selectors as ordersSelectors } from '@elab/core/store/orders';
import { selectors as configSelectors } from '@elab/core/store/config';
import logger from '@elab/core/utils/logger';
import { checkLoggedStatus } from '@elab/core/store/user/saga';

import { selectors as userSelectors, types as userTypes } from '../user';
import orderApi from '../order/api';

import actions from './actions';
import types from './types';
import api from './api';
import selectors from './selectors';

export function* loadOrders(page, pageSize, addPage, offlineOrders) {
    let userId = yield call(getUserId);

    if (!userId) {
        yield take(userTypes.SET_PROFILE);
        userId = yield select(userSelectors.getUserId);
    }

    yield put(actions.setLoading(true));
    yield put(orderActions.resetErrorCode());

    if (offlineOrders) {
        try {
            yield put(actions.setOfflineApiError(false));
            const dataResponse = yield call(api.getOfflineOrders, userId);
            const offlineOrders = dataResponse.orders || [];
            yield put(actions.setOfflineOrdersData(dataResponse));

            // Frontend pagination
            if (pageSize) {
                const pagination = yield call(getPagination, offlineOrders, 0, pageSize);
                yield put(actions.setCurrentPage(pagination));
            }
        } catch (e) {
            yield put(actions.setOfflineApiError(true));
            yield put(actions.setOfflineOrdersData({}));
        } finally {
            yield put(actions.setLoading(false));
        }
    } else {
        const statuses = yield select(configSelectors.getOrdersStatuses);

        try {
            const data = yield call(api.getOrders, userId, page, pageSize, statuses);
            if (addPage) {
                yield put(actions.addOrdersData(data));
            } else {
                yield put(actions.setOrdersData(data));
            }
        } catch (e) {
            yield call(logger.error, e);
        } finally {
            yield put(actions.setLoading(false));
        }
    }
}

export function* setCurrentPage(page, size) {
    const orders = yield select(selectors.getOfflineOrders);
    const pagination = yield call(getPagination, orders, page, size);
    yield put(actions.setCurrentPage(pagination));
}

export const getPagination = (orders = [], currentPage = 0, pageSize = 10) => ({
    totalPages: Math.ceil(orders.length / pageSize),
    totalResults: orders.length,
    offlineOrders: orders.slice(currentPage * pageSize, (currentPage + 1) * pageSize),
    currentPage,
    pageSize,
});

export function* getPage(action) {
    const { page, size, type, isAdditional = false } = action.payload;
    if (isAdditional) {
        yield call(setCurrentPage, page, size);
    } else {
        yield call(loadOrders, page, size, false, type);
    }
}

export function* addPage(action) {
    yield call(loadOrders, action.payload.page, action.payload.size, true);
}

export function* setPageSize(action) {
    yield call(loadOrders, null, action.payload);
}

export function* getOrders(action) {
    const isLogged = yield call(checkLoggedStatus);
    const { source, statusCode, pageNumber, eventType } = action.payload;
    if (isLogged) {
        let userId = yield call(getUserId);

        if (!userId) {
            yield take(userTypes.SET_PROFILE);
            userId = yield select(userSelectors.getUserId);
        }

        const _pageNumber = pageNumber || 0;

        try {
            const data = yield call(api.getOrders, userId, _pageNumber, 10, statusCode, source);
            if (Object.keys(data).length > 0) {
                const result = {};
                result[eventType] = data;
                yield put(actions.setOrdersForExtService(result));
            }

            const orders = yield select(ordersSelectors.getOrdersForExtService, eventType);
            const event = new CustomEvent(eventType, { detail: orders });
            window.dispatchEvent(event);
        } catch (e) {
            yield call(logger.error, e);
            window.dispatchEvent(new CustomEvent(`${eventType}Error`, { detail: e }));
        }
    }
}

export function* getOrder(action) {
    const isLogged = yield call(checkLoggedStatus);
    const { source, orderId, eventType } = action.payload;
    if (isLogged) {
        let userId = yield call(getUserId);

        if (!userId) {
            yield take(userTypes.SET_PROFILE);
            userId = yield select(userSelectors.getUserId);
        }

        try {
            const data = yield call(orderApi.getOrder, orderId, userId, source);
            if (Object.keys(data).length > 0) {
                const result = {};
                result[eventType] = data;
                yield put(actions.setOrderDetailForExtService(result));
            }

            const order = yield select(ordersSelectors.getOrderDetailForExtService, eventType);
            const event = new CustomEvent(eventType, { detail: order });
            window.dispatchEvent(event);
        } catch (e) {
            yield call(logger.error, e);
            window.dispatchEvent(new CustomEvent(`${eventType}Error`, { detail: e }));
        }
    }
}

function* addPageForOfflineOrders(action) {
    const data = action.payload || {};
    const { size } = data;
    const orders = yield select(selectors.getOfflineOrders);
    const additionalPagination = yield select(selectors.getOfflinePagination);
    const ordersInPaginationGrid = additionalPagination.offlineOrders || [];
    const page = additionalPagination.currentPage ? additionalPagination.currentPage + 1 : 1;
    const pagination = yield call(getPagination, orders, page, size);
    pagination.offlineOrders = [...ordersInPaginationGrid, ...pagination.offlineOrders];
    yield put(actions.setCurrentPage(pagination));
}

export default [
    debounce(400, types.LOAD_ORDERS_DATA, getPage),
    debounce(400, types.GET_PAGE, getPage),
    debounce(400, types.ADD_ONE_MORE_PAGE, addPage),
    debounce(400, types.ADD_ONE_MORE_PAGE_OFFLINE, addPageForOfflineOrders),
    debounce(400, types.SET_PAGE_SIZE, setPageSize),
    takeEvery(types.GET_ORDERS, getOrders),
    takeEvery(types.GET_ORDER, getOrder),
];
